import type { ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';
import styled, { css } from 'styled-components';
import { useMainMenuContext } from './MainMenuContext';

const itemActiveStyles = css`
  ${({ theme }) => css`
    color: ${theme.colors.primary.main};
    background: ${theme.colors.highlight.blue25};

    &:hover {
      color: ${theme.colors.primary.dark};
      background: ${theme.colors.highlight.blue40};
    }
  `}
`;

const itemStyles = css<{ $active: boolean; $disabled: boolean; $collapsed: boolean }>`
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 10px 16px;
  background: transparent;
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: ${({ theme }) => theme.fontSizes[3]}px;
  font-weight: 500;
  text-align: left;
  cursor: pointer;
  transition: 0.2s background-color ease-in-out;
  text-decoration: none;
  white-space: nowrap;
  overflow: hidden;

  &:hover {
    background: ${({ theme }) => theme.colors.highlight.gray25};
    color: ${({ theme }) => theme.colors.text.primary};
  }

  ${({ $active }) => $active && itemActiveStyles}

  ${({ $disabled }) =>
    $disabled &&
    css`
      opacity: 0.4;
      pointer-events: none;
    `}

  ${({ $collapsed }) =>
    $collapsed &&
    css`
      padding: 10px;
      justify-content: center;
    `}

  ${({ theme }) => theme.device.mobile} {
    min-height: 44px;
  }
`;

const LinkItem = styled(Link)<{ $active: boolean; $disabled: boolean; $collapsed: boolean }>`
  ${itemStyles}
`;

const ButtonItem = styled.button<{ $active: boolean; $disabled: boolean; $collapsed: boolean }>`
  border: none;
  font-family: inherit;
  ${itemStyles}
`;

export interface MenuItemProps {
  to?: string;
  exact?: boolean;
  icon?: ReactNode;
  active?: boolean;
  disabled?: boolean;
  onClick?: () => void;
  children: ReactNode;
}

export function MenuItem({ to, exact = false, icon, active, disabled = false, onClick, children }: MenuItemProps) {
  const { collapsed } = useMainMenuContext();
  const location = useLocation();

  const isActive = to != null
    ? (exact ? location.pathname === to : location.pathname.startsWith(to))
    : (active ?? false);

  const label = typeof children === 'string' ? children : undefined;

  const content = (
    <>
      {icon && (
        <span style={{ display: 'flex', flexShrink: 0 }}>
          {icon}
        </span>
      )}
      {!collapsed && (
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {children}
        </span>
      )}
    </>
  );

  if (to != null) {
    return (
      <LinkItem
        to={to}
        $active={isActive}
        $disabled={disabled}
        $collapsed={collapsed}
        onClick={onClick}
        title={collapsed ? label : undefined}
      >
        {content}
      </LinkItem>
    );
  }

  return (
    <ButtonItem
      type="button"
      $active={isActive}
      $disabled={disabled}
      $collapsed={collapsed}
      onClick={onClick}
      title={collapsed ? label : undefined}
    >
      {content}
    </ButtonItem>
  );
}
